/** @format */
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    inject,
    input,
    InputSignal,
    Signal,
    ViewContainerRef,
    ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialogRef } from '@angular/material/dialog';
import { EMPTY, map, Observable, of, switchMap, tap } from 'rxjs';
import { mono, RequestQueue, requestQueueIgnoreConcurrent } from 'sis-common';
import { AlertsService, AppErrorHandler, ConfirmDialogFdComponent, confirmDialogFdOpener, ConfirmDialogFdValues } from 'sis-components';
import { TimelineControlsService } from '../service/timeline-controls.service';
import { TimingActionService } from '../service/timing-action.service';
import { TimeCourseUnitModalComponent, timeCourseUnitModalOpener } from '../time-course-unit-modal/time-course-unit-modal.component';
import {
    TimingTemplateModalComponent,
    timingTemplateModalOpener,
} from '../timing-template/timing-template-modal/timing-template-modal.component';
import { StudyTermData } from '../timing.types';
import { SelectedPlanStudyRightService } from './selected-plan-study-right.service';
import { StudyPeriodDataService } from './study-period-data.service';

export type TimelineHeadingLevel = 1 | 2;
export type TimelineChildHeadingLevel = 2 | 3;
export type TimelineGrandChildHeadingLevel = 3 | 4;

@Component({
    selector: 'app-timeline',
    standalone: false,
    templateUrl: './timeline.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimelineComponent {
    readonly headingLevel: InputSignal<TimelineHeadingLevel> = input.required<TimelineHeadingLevel>();

    protected readonly studyTerms$: Observable<readonly StudyTermData[]> = inject(StudyPeriodDataService).studyTermData$;
    protected readonly childHeadingLevel: Signal<TimelineChildHeadingLevel> = computed(
        () => <TimelineChildHeadingLevel>(this.headingLevel() + 1),
    );
    protected readonly grandChildHeadingLevel: Signal<TimelineGrandChildHeadingLevel> = computed(
        () => <TimelineGrandChildHeadingLevel>(this.headingLevel() + 2),
    );

    protected readonly timelineHeadingId: string = 'timeline-heading';

    private readonly timelineControlsService = inject(TimelineControlsService);
    protected readonly useHorizontalLayout$: Observable<boolean> = this.timelineControlsService.useHorizontalLayout$;

    private readonly timingActionService = inject(TimingActionService);
    private readonly alertsService = inject(AlertsService);
    private readonly appErrorHandler = inject(AppErrorHandler);
    private readonly destroyRef = inject(DestroyRef);
    private readonly viewContainerRef = inject(ViewContainerRef);
    private readonly timingTemplateModalOpener: (viewContainerRef: ViewContainerRef) => MatDialogRef<TimingTemplateModalComponent, null> =
        timingTemplateModalOpener();
    private readonly timeCourseUnitModalOpener: (viewContainerRef: ViewContainerRef) => MatDialogRef<TimeCourseUnitModalComponent, null> =
        timeCourseUnitModalOpener();
    private readonly submitRemove: RequestQueue<void, void> = requestQueueIgnoreConcurrent<void, void>(
        () =>
            mono(
                this.confirmDialogFdOpener({
                    title: 'TIMELINE.REMOVE_TIMINGS_DIALOG.TITLE',
                    description: 'TIMELINE.REMOVE_TIMINGS_DIALOG.DESCRIPTION',
                    confirmText: 'TIMELINE.REMOVE_TIMINGS_DIALOG.DELETE',
                    confirmObservable: of(true),
                })
                    .afterClosed()
                    .pipe(switchMap(confirmed => this.afterDialogClosed(confirmed))),
            ),
        () => {},
        this.appErrorHandler.defaultErrorHandler(),
        takeUntilDestroyed(this.destroyRef),
    );

    private readonly confirmDialogFdOpener: (
        modalValues?: ConfirmDialogFdValues<boolean>,
    ) => MatDialogRef<ConfirmDialogFdComponent<boolean>, boolean> = confirmDialogFdOpener();

    private afterDialogClosed(result: boolean | undefined): Observable<void> {
        if (result) {
            return this.timingActionService
                .removeSelectedPlanTimings()
                .pipe(tap(() => this.alertsService.success('TIMELINE.REMOVE_TIMINGS_DIALOG.SUCCESS_ALERT')));
        }
        return EMPTY;
    }

    protected readonly studyRightValidityPeriod$ = inject(SelectedPlanStudyRightService).studyRight$.pipe(
        map(sr => sr?.valid),
        this.appErrorHandler.defaultErrorHandler(),
    );

    protected removeTimings(): void {
        this.submitRemove.next();
    }

    protected openTimingTemplateModal(): void {
        this.timingTemplateModalOpener(this.viewContainerRef);
    }

    protected openTimeCourseUnitModal(): void {
        this.timeCourseUnitModalOpener(this.viewContainerRef);
    }

    protected setUseHorizontalLayout(value: boolean): void {
        this.timelineControlsService.setUseHorizontalLayout(value);
    }
}
